import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface ApprovalStepDecision {
  approverName: string | null;
  decision: string;
  note: string | null;
  createdAt: string;
}

export interface ApprovalStepProgress {
  order: number;
  name: string;
  approverRole: string;
  slaHours: number;
  escalateAfterHours: number;
  status: "done" | "rejected" | "current" | "pending";
  decision: ApprovalStepDecision | null;
}

export interface ApprovalProgress {
  requestId: number;
  status: string;
  currentStep: number;
  totalSteps: number;
  currentStepName: string | null;
  escalated: boolean;
  reminded: boolean;
  stepStartedAt: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  steps: ApprovalStepProgress[];
}

interface UseApprovalProgressResult {
  items: Record<string, ApprovalProgress>;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

// 客户侧读取自己申请所处的多级审批级别（只读，按登录用户过滤）。
export function useApprovalProgress(): UseApprovalProgressResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [items, setItems] = useState<Record<string, ApprovalProgress>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setItems({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("approval-progress", { body: {} });
      if (fnError) throw fnError;
      const result = data as { items?: Record<string, ApprovalProgress>; error?: string } | null;
      if (result?.error) throw new Error(result.error);
      setItems(result?.items ?? {});
    } catch (err) {
      setError(err instanceof Error ? err.message : "审批进度加载失败");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  return { items, loading: loading || authLoading, error, reload: load };
}